/**
 * 진단 지표의 표시 이름과 설명. 제품 카피이며 FE 가 소유한다.
 *
 * 이 화면은 "무엇을 보고 있는지 모르겠다"는 피드백에서 시작했다(ZERG-290). 지표 이름이
 * JSX 안에 흩어져 있으면 이름과 설명이 서로 맞지 않고, 같은 값을 자리마다 다르게 부른다.
 * 이 모듈은 라벨과 설명을 짝지어 제공한다. 캡션과 툴팁은 이 사전만 읽는다
 * (`components/label/episodeQualityTags.ts` 와 같은 구조).
 *
 * 이 모듈이 소유하지 않는 것:
 * - 상태·실패 문구. `diagnosticsStatusText.ts` 가 소유한다(무엇을 그리지 못했는지 알리는 문구).
 * - 채널 이름. 서버 구조 응답이 SoT 다(`parquetChannels.channelTitle`).
 * - 임계값. `bandDeviation.ts` 의 상수가 SoT 이다. 여기서는 그 값을 문장에 인용만 한다.
 */

import { DEVIATION_ALERT_FRACTION, DEVIATION_WARN_FRACTION } from "./bandDeviation";
import type { ParquetChannelGroupId } from "./parquetChannels";

export type DiagnosticsTerm = {
  // 화면에 표시하는 이름.
  label: string;
  // 그 이름 옆 툴팁 한 문장. "이 숫자가 무엇을 재는가"에 답한다.
  tip: string;
};

/**
 * 기준 밴드는 같은 데이터셋 에피소드들(이 에피소드 포함)의 값을 성분·위상 지점마다 모은 p10–p90 이다.
 * 7D pose 는 위치 세 성분과, 대표 회전까지의 p90 각도 반경을 따로 비교한다.
 *
 * "기준 밴드 밖 비율"이라는 이름으로는 무엇과 무엇을 비교하는지 알 수 없다. 그래서 화면에는
 * "다른 에피소드와 다른 정도"로 표시한다. 문구는 이것이 판정이 아니라 비교라는 점도 알려 준다.
 */
export const DIAGNOSTICS_TERMS = {
  deviation: {
    label: "다른 에피소드와 다른 정도",
    tip: "같은 Dataset의 성분별 p10–p90 범위와 7D 자세의 p90 회전각 반경을 벗어난 비교점의 비율입니다. 높다고 잘못된 데이터라는 뜻은 아니고, 먼저 확인해 볼 곳을 알려 줍니다.",
  },
  actionDeviation: {
    label: "다르게 움직인 정도",
    tip: "로봇에 내린 Action이 같은 Dataset의 다른 에피소드들과 얼마나 달랐는지입니다. 높으면 조작 방식이 달랐거나 작업이 뜻대로 안 풀린 구간이 있습니다.",
  },
  observationDeviation: {
    label: "다른 상황을 본 정도",
    tip: "Observation이 같은 Dataset의 다른 에피소드들과 얼마나 달랐는지입니다. 높으면 물체 위치나 시작 자세처럼 촬영 상황이 달랐을 수 있습니다.",
  },
  referenceBand: {
    label: "기준 통로",
    tip: "같은 Dataset 에피소드들(이 에피소드 포함)의 성분별 p10–p90 범위입니다. 7D 자세는 대표 회전에서 각도 0~p90 반경을 사용합니다. 이 에피소드를 견주는 잣대입니다.",
  },
  bandWidth: {
    label: "기준 통로 폭",
    tip: "기준 통로가 얼마나 넓은지입니다. 넓을수록 원래 에피소드마다 편차가 큰 채널이라 조금 벗어나는 것이 덜 특별합니다.",
  },
  actionVariance: {
    label: "행동 분산",
    tip: "로봇에 내린 Action 이 에피소드 안에서 얼마나 크게 움직였는지입니다. 낮으면 거의 멈춰 있었다는 뜻입니다.",
  },
  stateSimilarity: {
    label: "상태 유사도",
    tip: "Observation 상태가 다른 에피소드들과 얼마나 닮았는지입니다. 낮으면 다른 상황을 담은 에피소드일 수 있습니다.",
  },
} as const satisfies Record<string, DiagnosticsTerm>;

export type DiagnosticsTermKey = keyof typeof DIAGNOSTICS_TERMS;

/**
 * 채널 그룹 머리글. 같은 이름의 채널이 topic 마다 하나씩 표시되므로 필요하다.
 *
 * `action::ee_pose` 와 `observation_vector::ee_pose` 는 표시명이 같다. 두 곡선의 모양이 비슷한
 * 데이터셋에서 머리글이 없으면, 사용자는 같은 것을 두 번 그렸다고 이해한다. 이 머리글은
 * 실제로 그 피드백 때문에 추가했다.
 *
 * 이름은 **영어로 고정한다**. `Action`·`Observation` 은 topic·API·팀 대화가 함께 쓰는 공유
 * 명사다. 번역하면 화면 용어만 달라진다(`docs/rules.md` §11). 그룹이 무엇을 담는지는 tip 이 설명한다.
 */
export const DIAGNOSTICS_GROUP_TERMS = {
  observation: {
    label: "Observation",
    tip: "로봇이 실제로 어디에 있었는지 센서가 잰 값입니다. 실제로 일어난 일이라 속력·가속도는 모두 이 값에서 나옵니다. Action 에 이름이 같은 채널이 있으면 같은 곳을 잰 것이라 나란히 비교할 수 있습니다.",
  },
  action: {
    label: "Action",
    tip: "로봇에게 “이 자리로 가라”고 보낸 목표값입니다. 아직 일어난 일이 아니라 시키려는 일입니다. Observation 에 이름이 같은 채널이 있으면 같은 곳을 가리키므로, 두 값의 차이가 시킨 대로 따라갔는지를 말해 줍니다.",
  },
  other: {
    label: "Other",
    tip: "시킨 값도 잰 값도 아닌 채널입니다. 그때그때의 상태나 부가 기록이 여기에 듭니다.",
  },
} as const satisfies Record<ParquetChannelGroupId, DiagnosticsTerm>;

export function diagnosticsTermLabel(key: DiagnosticsTermKey): string {
  return DIAGNOSTICS_TERMS[key].label;
}

export function diagnosticsTermTip(key: DiagnosticsTermKey): string {
  return DIAGNOSTICS_TERMS[key].tip;
}

/**
 * 진단 표면의 출처. 캡션 첫 문장이고, FE 에서 논문을 표기하는 유일한 자리다. 업로드 폼의
 * 진단 토글은 한 줄 설명만 가진다. 이 표기는 지표별로 나누지 않은 묶음 표기다. 정확한 귀속
 * 범위는 BE 스펙(`docs/specs/dataset-learning-diagnostics.md` §4)이 SoT 다. 논문 지표는
 * 행동 분산·상태 유사도이고, 기준 통로는 분위수 통계다.
 */
export const DIAGNOSTICS_PAPER_NOTE =
  "Belkhale et al. 2023 (Data Quality in Imitation Learning, NeurIPS) 기반 Training 진단";

/** 범례 스와치. band 는 기준 통로(회색)다. 나머지는 이탈 심각도 색(ok/warn/alert)과 같다. */
export type DiagnosticsLegendSwatch = "band" | "ok" | "warn" | "alert";

const WARN_PERCENT = Math.round(DEVIATION_WARN_FRACTION * 100);
const ALERT_PERCENT = Math.round(DEVIATION_ALERT_FRACTION * 100);

/**
 * 기준 통로가 있을 때의 차트 범례. 산문 대신 색 동그라미로 표시한다. 색·임계·뜻이 이 한 곳에
 * 모인다. 그래서 차트 머리에 별도 범례를 두지 않는다.
 *
 * 회색 기준 통로 설명 뒤에 "이 에피소드의 trajectory:"를 소제목으로 두고, 세 색 구간을
 * 한 줄씩 나눈다. 주어를 한 번만 적어 "이 에피소드의 trajectory가 …" 반복을 없앤다.
 */
export const DIAGNOSTICS_LEGEND_BAND_TEXT =
  "전체 에피소드 trajectory의 성분·위상 지점별 p10–p90 범위(기준 통로)입니다. 회전각(rad)은 대표 자세에서 0~p90 반경이며 점선은 각 분포의 중앙값입니다";

export const DIAGNOSTICS_LEGEND_EPISODE_LABEL = "이 에피소드의 trajectory:";

export const DIAGNOSTICS_LEGEND_EPISODE_ITEMS: readonly {
  swatch: Exclude<DiagnosticsLegendSwatch, "band">;
  text: string;
}[] = [
  { swatch: "ok", text: `구역 안(기준 밖 <${WARN_PERCENT}%)` },
  { swatch: "warn", text: `자주 벗어남(${WARN_PERCENT}–${ALERT_PERCENT}%)` },
  { swatch: "alert", text: `크게 벗어남(≥${ALERT_PERCENT}%)` },
];

/**
 * 축을 잴 수 없을 때의 한 줄. 값이 **0% 가 아니라 "없음"** 이라는 사실을 알려 준다.
 *
 * prod 기준으로 에피소드의 16.5% 에 action 축이 없다(`action/` 에 `done` 만 있는 직접교시 계열).
 * 그 칸을 0% 처럼 칠하면 사용자는 "가장 잘 맞은 에피소드"로 이해한다. 그래서 칸은 회색으로
 * 비우고, 비어 있는 이유는 이 문구가 알려 준다.
 */
export function absentAxisTip(axis: "action" | "observation"): string {
  const name = axis === "action" ? "Action" : "Observation";
  return `${name} 채널이 없어 재지 못했습니다. 값이 0%인 것과 다릅니다.`;
}

/** 축 칸 하나의 툴팁. 이탈률과, 그 축에서 가장 많이 벗어난 채널을 표시한다. */
export function axisDeviationTip(
  axis: "action" | "observation",
  fraction: number,
  worstChannel: string | null,
  worstFraction: number | null,
): string {
  const term =
    axis === "action" ? DIAGNOSTICS_TERMS.actionDeviation : DIAGNOSTICS_TERMS.observationDeviation;
  const head = `${term.label} ${Math.round(fraction * 100)}%`;
  if (worstChannel === null || worstFraction === null) {
    return `${head} · ${term.tip}`;
  }
  return `${head} · 가장 많이 벗어난 채널은 ${worstChannel}(${Math.round(worstFraction * 100)}%)입니다.`;
}

/**
 * 기준 통로가 아직 없으면(에피소드가 적거나 산출 전) 비교할 대상이 없다. 그래서 이 에피소드만
 * 그린다. 그 사실을 숨기지 않는다. 색 구간이 없으므로 범례 대신 산문 한 줄을 표시한다.
 */
export const DIAGNOSTICS_CHART_CAPTION_WITHOUT_BAND =
  "색 = 이 에피소드 각 채널의 범위와 평균입니다. 견줄 기준 통로가 아직 없어 이 에피소드만 그립니다.";

/**
 * 가로축이 시간이 아니라는 안내. 클릭으로 영상을 옮길 수 있으므로 이 안내가 필요하다.
 *
 * 진단 아티팩트의 가로축은 행 위상(0→1 정규화)이다(`diagnosticsSeries.ts`). 그래서 프레임 수가
 * 다른 에피소드끼리 겹칠 수 있다. 영상 시간으로 옮길 때는 길이를 곱해 변환한다. 그래서 기록
 * 주기가 일정하지 않은 구간에서는 몇 프레임 어긋날 수 있다.
 */
export const DIAGNOSTICS_SEEK_HINT =
  "가로축은 에피소드를 0~100%로 편 진행도입니다. 그래프를 누르면 영상이 그 지점으로 이동합니다.";

/**
 * 구간 음영과 재생선을 겹쳐 그렸을 때의 안내. 위 단락의 이유로 두 가로축의 위치는 서로 같지 않다.
 * 이 문구는 "음영 위치로 구간 값을 재도 되나"라는 오해를 막는다. 값을 재려면 구간 보기의 표를
 * 기준으로 쓴다.
 */
export const DIAGNOSTICS_SHADING_NOTE =
  "음영은 사람이 낸 구간, 세로선은 재생 위치입니다. 영상 시각을 진행도로 옮긴 자리라 대략의 위치입니다.";
